/**
 * filingTracker.ts — Pure computation of GST filing status per period: which
 * GSTR-1 and GSTR-3B returns are filed, pending, or overdue, against the
 * statutory monthly due dates.
 *
 * Due dates (monthly filer): GSTR-1 by the 11th of the following month, GSTR-3B
 * by the 20th. A return is FILED when a filing record marks it so; otherwise it
 * is OVERDUE once its due date has passed, else PENDING.
 *
 * This file is schema-independent: it takes filing records and the list of
 * periods to show. The persistence of those records (the `gst_filings` table)
 * is the separate, schema-authorised part of the feature.
 *
 * Pure — see `filingTrackerDataSource.ts` for the WatermelonDB bridge.
 */

export type GstReturnType = 'GSTR1' | 'GSTR3B';
export type FilingStatus = 'FILED' | 'PENDING' | 'OVERDUE';

/** Statutory due day of the *following* month, by return type. */
const DUE_DAY: Record<GstReturnType, number> = { GSTR1: 11, GSTR3B: 20 };

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface FilingRecord {
  /** 'YYYY-MM'. */
  period: string;
  returnType: GstReturnType;
  /** 'FILED' marks the return done; anything else is treated as not filed. */
  status: string;
  filedAt: Date | null;
  arn: string | null;
}

export interface FilingTrackerInput {
  filings: FilingRecord[];
  /** Periods to display, 'YYYY-MM'. */
  periods: string[];
  today: Date;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface ReturnCell {
  status: FilingStatus;
  filedAt: Date | null;
  arn: string | null;
  dueDate: Date;
}

export interface FilingPeriodRow {
  period: string;
  gstr1: ReturnCell;
  gstr3b: ReturnCell;
}

export interface FilingTracker {
  /** Newest period first. */
  rows: FilingPeriodRow[];
  summary: { filed: number; pending: number; overdue: number };
}

/** Statutory due date of `returnType` for a 'YYYY-MM' period. */
export function dueDateFor(returnType: GstReturnType, period: string): Date {
  const [y, m] = period.split('-').map(Number);
  // m is 1-based; new Date(y, m, day) rolls to the *following* month.
  return new Date(y, m, DUE_DAY[returnType]);
}

function cellFor(
  returnType: GstReturnType,
  period: string,
  byKey: Map<string, FilingRecord>,
  today: Date,
): ReturnCell {
  const dueDate = dueDateFor(returnType, period);
  const rec = byKey.get(`${period}|${returnType}`);
  if (rec && rec.status === 'FILED') {
    return { status: 'FILED', filedAt: rec.filedAt, arn: rec.arn, dueDate };
  }
  const status: FilingStatus = today.getTime() > dueDate.getTime() ? 'OVERDUE' : 'PENDING';
  return { status, filedAt: null, arn: null, dueDate };
}

export function buildFilingTracker(input: FilingTrackerInput): FilingTracker {
  const { filings, periods, today } = input;

  const byKey = new Map<string, FilingRecord>();
  for (const f of filings) byKey.set(`${f.period}|${f.returnType}`, f);

  const rows: FilingPeriodRow[] = [...periods]
    .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0)) // newest first
    .map((period) => ({
      period,
      gstr1: cellFor('GSTR1', period, byKey, today),
      gstr3b: cellFor('GSTR3B', period, byKey, today),
    }));

  const summary = { filed: 0, pending: 0, overdue: 0 };
  for (const row of rows) {
    for (const cell of [row.gstr1, row.gstr3b]) {
      if (cell.status === 'FILED') summary.filed += 1;
      else if (cell.status === 'OVERDUE') summary.overdue += 1;
      else summary.pending += 1;
    }
  }

  return { rows, summary };
}

/** Generate the last `count` 'YYYY-MM' periods ending at `anchor`'s month. */
export function recentPeriods(anchor: Date, count: number): string[] {
  const periods: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
    periods.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return periods;
}
